import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useInView, animate } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

/** Subtle radial glow that follows the cursor. Dark theme only; skips touch. */
export function CursorSpotlight() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const move = (e) => {
      if (e.pointerType === "touch") return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.background = `radial-gradient(520px at ${e.clientX}px ${e.clientY}px, color-mix(in srgb, var(--dt-accent) 8%, transparent), transparent 70%)`;
      });
    };
    window.addEventListener("pointermove", move, { passive: true });
    return () => { window.removeEventListener("pointermove", move); cancelAnimationFrame(raf); };
  }, [reduced]);
  if (reduced) return null;
  return <div ref={ref} className="cursor-spotlight pointer-events-none fixed inset-0 z-[1]" aria-hidden="true" data-testid="cursor-spotlight" />;
}

/** WebGL-style animated liquid background for Fluid Dark (Dribbble-inspired):
 * deep near-black with drifting deep-blue volumetric blobs and a subtle
 * mouse-reactive glow. Canvas-rendered at low res + CSS blur for a liquid feel. */
export function FluidCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const SCALE = 0.35; // low-res render, CSS blur smooths it into liquid
    let w = 0; let h = 0; let raf = 0; let running = true;
    const mouse = { x: 0.5, y: 0.4, tx: 0.5, ty: 0.4 };

    const blobs = [
      { c: [11, 32, 137], r: 0.42, ax: 0.30, ay: 0.24, sx: 0.00016, sy: 0.00011, px: 0.0, py: 1.3, cx: 0.32, cy: 0.34 },
      { c: [17, 27, 85], r: 0.52, ax: 0.26, ay: 0.30, sx: 0.00011, sy: 0.00015, px: 2.1, py: 0.4, cx: 0.68, cy: 0.30 },
      { c: [42, 75, 215], r: 0.30, ax: 0.34, ay: 0.20, sx: 0.00019, sy: 0.00013, px: 4.2, py: 2.6, cx: 0.55, cy: 0.72 },
      { c: [8, 18, 58], r: 0.58, ax: 0.22, ay: 0.26, sx: 0.00009, sy: 0.00012, px: 1.1, py: 3.8, cx: 0.24, cy: 0.78 },
      { c: [58, 98, 235], r: 0.22, ax: 0.38, ay: 0.30, sx: 0.00023, sy: 0.00017, px: 5.3, py: 1.9, cx: 0.78, cy: 0.62 },
    ];

    const resize = () => {
      w = canvas.width = Math.max(2, Math.floor(window.innerWidth * SCALE));
      h = canvas.height = Math.max(2, Math.floor(window.innerHeight * SCALE));
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      mouse.tx = e.clientX / window.innerWidth;
      mouse.ty = e.clientY / window.innerHeight;
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    const onVis = () => {
      running = !document.hidden;
      if (running) raf = requestAnimationFrame(draw);
    };
    document.addEventListener("visibilitychange", onVis);

    const draw = (t) => {
      if (!running) return;
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#010103";
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";
      for (const b of blobs) {
        const x = (b.cx + Math.sin(t * b.sx + b.px) * b.ax) * w;
        const y = (b.cy + Math.cos(t * b.sy + b.py) * b.ay) * h;
        const rad = b.r * Math.max(w, h) * (1 + 0.08 * Math.sin(t * 0.0002 + b.px));
        const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
        g.addColorStop(0, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},0.55)`);
        g.addColorStop(0.55, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},0.18)`);
        g.addColorStop(1, "rgba(1,1,3,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, rad, 0, Math.PI * 2);
        ctx.fill();
      }
      // mouse-reactive glow, eased
      mouse.x += (mouse.tx - mouse.x) * 0.06;
      mouse.y += (mouse.ty - mouse.y) * 0.06;
      const mx = mouse.x * w; const my = mouse.y * h;
      const mr = 0.34 * Math.max(w, h);
      const mg = ctx.createRadialGradient(mx, my, 0, mx, my, mr);
      mg.addColorStop(0, "rgba(76,111,255,0.16)");
      mg.addColorStop(1, "rgba(1,1,3,0)");
      ctx.fillStyle = mg;
      ctx.beginPath();
      ctx.arc(mx, my, mr, 0, Math.PI * 2);
      ctx.fill();
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <>
      <canvas ref={ref} aria-hidden="true" data-testid="fluid-canvas"
        className="pointer-events-none fixed inset-0 z-0 w-full h-full"
        style={{ filter: "blur(46px) saturate(1.25)", transform: "scale(1.12)", opacity: 0.85 }} />
      {/* readability scrim — keeps text crisp over the animated liquid */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true"
        style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(1,1,3,0.30) 0%, rgba(1,1,3,0.55) 100%)" }} />
    </>
  );
}

/** Animated backdrop for the two premium dark themes — fluid marble swirl
 * (Fluid Dark) or glowing aurora orbs (Aurora Dark). Fixed, behind content. */
export function ThemeBackdrop() {
  const { theme } = useTheme();
  const reduced = useReducedMotion();
  if (theme === "light") return null;
  if (reduced) {
    return <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true"
      style={{ background: theme === "aurora" ? "radial-gradient(80% 60% at 30% 0%, rgba(99,102,241,.14), transparent), #070b18" : "#010103" }} />;
  }
  if (theme === "fluid") return <FluidCanvas />;
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true" data-testid="theme-backdrop">
      <div className="aurora-orb aurora-a" />
      <div className="aurora-orb aurora-b" />
      <div className="aurora-orb aurora-c" />
    </div>
  );
}

/** Slow-shifting animated gradient mesh for hero backgrounds. */
export function GradientMesh({ className = "" }) {
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden="true">
      <div className="mesh-blob mesh-1" style={{ width: "48vw", height: "48vw", left: "-12vw", top: "-16vw", background: "radial-gradient(circle, color-mix(in srgb, var(--dt-accent) 30%, transparent), transparent 65%)" }} />
      <div className="mesh-blob mesh-2" style={{ width: "40vw", height: "40vw", right: "-10vw", top: "4vh", background: "radial-gradient(circle, color-mix(in srgb, var(--dt-silver) 18%, transparent), transparent 65%)" }} />
      <div className="mesh-blob mesh-3" style={{ width: "36vw", height: "36vw", left: "26vw", bottom: "-18vw", background: "radial-gradient(circle, color-mix(in srgb, var(--dt-accent) 20%, transparent), transparent 65%)" }} />
    </div>
  );
}

/** Chrome metallic shimmer text (hero headlines). */
export function ShimmerText({ children, className = "" }) {
  return <span className={`shimmer-text ${className}`}>{children}</span>;
}

/** Time-aware greeting bar for dashboards. Falls back to the logged-in user's name. */
export function GreetingBar({ name, subtitle = "", testid = "greeting-bar" }) {
  const { user } = useAuth();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const resolved = (name || user?.full_name || "").split(" ")[0] || "there";
  const dateStr = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="mb-6 flex flex-wrap items-end justify-between gap-2 border-b border-dt-border pb-5" data-testid={testid}>
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-dt-text">
          {greeting}, <span className="text-metallic">{resolved}</span>
        </h1>
        {subtitle && <p className="text-sm text-dt-muted mt-1">{subtitle}</p>}
      </div>
      <p className="text-xs text-dt-muted">{dateStr}</p>
    </motion.div>
  );
}

/** Dashboard card: fades up with a one-time inner glow pulse. */
export function GlowCard({ children, className = "", delay = 0, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`card-dt card-glow-once ${className}`}
      style={{ animationDelay: `${delay + 0.15}s` }}
      data-testid={testid}
    >
      {children}
    </motion.div>
  );
}

/** Odometer-style rolling digit count-up (starts when scrolled into view). */
export function Odometer({ value = 0, prefix = "", suffix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20px" });
  const reduced = useReducedMotion();
  const shown = inView || reduced ? value : 0;
  const str = Number(shown).toLocaleString("en-IN");
  return (
    <span ref={ref} className={`inline-flex items-baseline ${className}`} aria-label={`${prefix}${value}${suffix}`}>
      {prefix && <span>{prefix}</span>}
      {str.split("").map((ch, i) =>
        ch >= "0" && ch <= "9" ? (
          <span key={i} className="odo-digit">
            <span className="odo-strip" style={{ transform: `translateY(-${Number(ch)}em)` }}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => <span key={d} style={{ height: "1em" }}>{d}</span>)}
            </span>
          </span>
        ) : (
          <span key={i}>{ch}</span>
        )
      )}
      {suffix && <span>{suffix}</span>}
    </span>
  );
}

/** Smooth fade-slide wrapper for route transitions. */
export function PageFade({ children, className = "" }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}
